import { Box, useTheme } from "@mui/material";
import React from "react";
import { v4 as uuid } from "uuid";
import { DatePicker } from "../DatePicker/DatePicker";
import { getDate, isDateWithinRange } from "../Utils/DateUtils";
DateRangePicker.defaultProps = {
    disabled: false,
    hasError: false
};
export function DateRangePicker(props) {
    const { fromLabel, maxDate, minDate, toLabel, hasError, validationText, style, className, id, onChange, selectedFromDate, selectedToDate } = props;
    const theme = useTheme();
    const [internalError, setInternalError] = React.useState(undefined);
    const initialDateRange = { minDate: minDate, maxDate: maxDate };
    const [minMaxDateFrom, setMinMaxDateFrom] = React.useState(initialDateRange);
    const [minMaxDateTo, setMinMaxDateTo] = React.useState(initialDateRange);
    const [fromDate, setFromDate] = React.useState(null);
    const [toDate, setToDate] = React.useState(null);
    const fromLabelId = `${props.id ?? uuid()}-from-label`;
    const toLabelId = `${props.id ?? uuid()}-to-label`;
    const fromDatePickerId = `${props.id ?? uuid()}-from-picker`;
    const toDatePickerId = `${props.id ?? uuid()}-to-picker`;
    const fromLabelProps = fromLabel ? { "aria-labelledby": fromLabelId } : { "aria-label": "From" };
    const toLabelProps = toLabel ? { "aria-labelledby": toLabelId } : { "aria-label": "To" };
    //Retrieve error text from DatePicker and set DateRangePicker error state
    const validationError = (errorText) => {
        errorText ? setInternalError(errorText) : setInternalError(undefined);
    };
    //Run callback only after a little delay to ensure user has entered enough and is ready to submit.
    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (onChange) {
                onChange(fromDate, toDate);
            }
        }, 200);
        return () => {
            clearTimeout(timer);
        };
    }, [fromDate, toDate]);
    //Set internal FromDate state when prop value changed
    React.useEffect(() => {
        setFromDate(getDate({ value: selectedFromDate, throwOnError: false }));
    }, [selectedFromDate]);
    //Set internal toDate state when prop value changed
    React.useEffect(() => {
        setToDate(getDate({ value: selectedToDate, throwOnError: false }));
    }, [selectedToDate]);
    //Set min-max date on From field and handle internal state
    const handleOnChangeFrom = (date) => {
        const isValidDate = isDateWithinRange(date, minDate, maxDate);
        if (isValidDate) {
            setMinMaxDateTo({ minDate: date, maxDate: maxDate });
        }
        else {
            setMinMaxDateTo(initialDateRange);
        }
        setFromDate(date);
    };
    //Set min-max date on the to field and handle internal state
    const handleOnChangeTo = (date) => {
        const isValidDate = isDateWithinRange(date, minDate, maxDate);
        if (isValidDate) {
            setMinMaxDateFrom({ minDate: minDate, maxDate: date });
        }
        else {
            setMinMaxDateFrom(initialDateRange);
        }
        setToDate(date);
    };
    return (React.createElement(Box, { id: id, style: { ...style, display: "flex", flexDirection: "column" }, className: className },
        React.createElement(Box, { sx: {
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center"
            } },
            React.createElement(Box, { "data-testid": "date-range-picker-validation-label", sx: {
                    color: theme.palette.error.main,
                    visibility: hasError ? "visible" : "hidden",
                    minWidth: fromLabel ? 5 : 2,
                    width: fromLabel ? 7 : 2,
                    alignSelf: "center",
                    fontSize: "1.125rem"
                } }, "!"),
            React.createElement("label", { id: fromLabelId, style: { paddingRight: 5, fontSize: "inherit", display: fromLabel ? "inline-block" : "none" }, htmlFor: fromDatePickerId }, fromLabel),
            React.createElement(Box, { "data-testid": "date-range-picker-from-date-container", style: { marginRight: "8px" } }, props.locale ? (React.createElement(DatePicker, { ...fromLabelProps, minDate: minMaxDateFrom.minDate, maxDate: minMaxDateFrom.maxDate, selectedDate: selectedFromDate, onChange: handleOnChangeFrom, onInternalError: validationError, disabled: props.disabled, inputId: fromDatePickerId, hideValidationText: true, locale: props.locale })) : (React.createElement(DatePicker, { ...fromLabelProps, minDate: minMaxDateFrom.minDate, maxDate: minMaxDateFrom.maxDate, selectedDate: selectedFromDate, onChange: handleOnChangeFrom, onInternalError: validationError, dateFormat: props.dateFormat, disabled: props.disabled, inputId: fromDatePickerId, hideValidationText: true }))),
            React.createElement("label", { id: toLabelId, style: { padding: "0px 5px", fontSize: "inherit", display: toLabel ? "inline-block" : "none" }, htmlFor: toDatePickerId }, toLabel),
            React.createElement(Box, { "data-testid": "date-range-picker-to-date-container" }, props.locale ? (React.createElement(DatePicker, { ...toLabelProps, minDate: minMaxDateTo.minDate, maxDate: minMaxDateTo.maxDate, selectedDate: selectedToDate, onChange: handleOnChangeTo, onInternalError: validationError, disabled: props.disabled, inputId: toDatePickerId, hideValidationText: true, locale: props.locale })) : (React.createElement(DatePicker, { ...toLabelProps, minDate: minMaxDateTo.minDate, maxDate: minMaxDateTo.maxDate, selectedDate: selectedToDate, onChange: handleOnChangeTo, onInternalError: validationError, dateFormat: props.dateFormat, disabled: props.disabled, inputId: toDatePickerId, hideValidationText: true })))),
        React.createElement(Box, { sx: {
                fontSize: "0.687rem",
                minHeight: ".5rem",
                maxHeight: ".5rem",
                paddingTop: ".25rem",
                paddingLeft: fromLabel ? ".5rem" : 0,
                visibility: internalError || hasError ? "visible" : "hidden",
                color: theme.palette.error.main
            }, "data-testid": "date-range-picker-validation-text" }, internalError ?? validationText)));
}
